import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, Text, TextInput, View } from 'react-native';
import { BottomSheetTextInput } from '@gorhom/bottom-sheet';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { iconSize } from '../../../utils/libs';
import InventoryPanel from './InventoryPanel';
import SheetHeader from '../../../components/Reusables/bottomSheet/SheetHeader';
import { InventoryButton } from './InventoryParts';
import type { InventoryStyles } from './inventoryStyles';
import type { InventoryCategory } from './useInventoryCategories';
import { UNCATEGORISED_LABEL } from '../../../utils/inventoryFilters';
import {
  categoryKey,
  countSelectedCategories,
  getCategoryState,
  isSubCategorySelected,
  toggleCategoryNode,
  toggleSubCategory,
  type CategorySelection,
} from '../../../utils/inventoryCategorySelection';

type Props = {
  styles: InventoryStyles;
  theme: any;
  open: boolean;
  categories: InventoryCategory[];
  selection: CategorySelection;
  onDone: (selection: CategorySelection) => void;
  onClose: () => void;
};

// Native needs the sheet-aware input so the keyboard doesn't cover the list.
const SearchInput: any =
  Platform.OS === 'web' ? TextInput : BottomSheetTextInput;

/**
 * Full-height picker: every category with its subcategories, checkboxes and
 * product counts. Done hands the selection back to the Filters sheet.
 */
const InventoryCategoryPicker: React.FC<Props> = ({
  styles,
  theme,
  open,
  categories,
  selection,
  onDone,
  onClose,
}) => {
  const [draft, setDraft] = useState<CategorySelection>(selection);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      setDraft(selection);
      setQuery('');
      // Start with the categories that already have something ticked open
      setExpanded(
        categories
          .filter(node => getCategoryState(selection, node) === 'mixed')
          .map(categoryKey),
      );
    }
  }, [open, selection, categories]);

  const visible = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return categories;
    return categories
      .map(node => {
        const label = (node.category || UNCATEGORISED_LABEL).toLowerCase();
        if (label.includes(term)) return node;
        const subCategories = node.subCategories.filter(sub =>
          sub.name.toLowerCase().includes(term),
        );
        return subCategories.length ? { ...node, subCategories } : null;
      })
      .filter(Boolean) as InventoryCategory[];
  }, [categories, query]);

  const selectedCount = countSelectedCategories(draft);
  const searching = query.trim().length > 0;

  const checkbox = (state: 'on' | 'off' | 'mixed') => (
    <View style={[styles.checkbox, state !== 'off' && styles.checkboxOn]}>
      {state === 'on' ? (
        <MaterialCommunityIcons
          name="check"
          size={iconSize(16)}
          color={theme.colors.textInverse}
        />
      ) : state === 'mixed' ? (
        <MaterialCommunityIcons
          name="minus"
          size={iconSize(16)}
          color={theme.colors.textInverse}
        />
      ) : null}
    </View>
  );

  return (
    <InventoryPanel
      styles={styles}
      open={open}
      onClose={onClose}
      snapPoints={['92%']}
      header={
        <>
          <SheetHeader
            title="Categories"
            onBack={onClose}
            note={selectedCount ? `${selectedCount} selected` : undefined}
          />
          <View style={styles.pickerSearch}>
            <View style={styles.pickerField}>
              <MaterialCommunityIcons
                name="magnify"
                size={iconSize(20)}
                color={theme.colors.textSecondary}
              />
              <SearchInput
                value={query}
                onChangeText={setQuery}
                placeholder="Search categories"
                placeholderTextColor={theme.colors.textSecondary}
                style={styles.pickerInput}
                autoCorrect={false}
                accessibilityLabel="Search categories"
              />
              {query ? (
                <Pressable
                  onPress={() => setQuery('')}
                  accessibilityLabel="Clear search"
                  hitSlop={8}>
                  <MaterialCommunityIcons
                    name="close-circle"
                    size={iconSize(18)}
                    color={theme.colors.textSecondary}
                  />
                </Pressable>
              ) : null}
            </View>
          </View>
        </>
      }
      footer={
        <>
          <InventoryButton
            styles={styles}
            theme={theme}
            label="Clear"
            variant="ghost"
            onPress={() => setDraft({ categories: [], subCategories: [] })}
          />
          <InventoryButton
            styles={styles}
            theme={theme}
            label="Done"
            onPress={() => onDone(draft)}
            style={styles.sheetFooterPrimary}
          />
        </>
      }>
      <View>
        {visible.length === 0 ? (
          <Text style={styles.pickerEmpty}>
            {categories.length
              ? `No categories match “${query.trim()}”`
              : 'No categories yet'}
          </Text>
        ) : null}
        {visible.map(node => {
          const key = categoryKey(node);
          const state = getCategoryState(draft, node);
          const hasSubs = node.subCategories.length > 0;
          const isOpen = searching || expanded.includes(key);
          return (
            <View key={key}>
              <View style={styles.categoryRow}>
                <Pressable
                  onPress={() => setDraft(toggleCategoryNode(draft, node))}
                  accessibilityRole="checkbox"
                  accessibilityState={{
                    checked: state === 'mixed' ? 'mixed' : state === 'on',
                  }}
                  style={({ pressed }) => [
                    styles.categoryLabel,
                    pressed && styles.buttonPressed,
                  ]}>
                  {checkbox(state)}
                  <Text style={styles.categoryText} numberOfLines={1}>
                    {node.category || UNCATEGORISED_LABEL}
                  </Text>
                  <Text style={styles.categoryCount}>{node.products}</Text>
                </Pressable>
                {hasSubs ? (
                  <Pressable
                    onPress={() =>
                      setExpanded(current =>
                        current.includes(key)
                          ? current.filter(item => item !== key)
                          : [...current, key],
                      )
                    }
                    accessibilityRole="button"
                    accessibilityLabel={`${
                      isOpen ? 'Hide' : 'Show'
                    } subcategories of ${node.category}`}
                    style={styles.expandButton}>
                    <MaterialCommunityIcons
                      name={isOpen ? 'chevron-up' : 'chevron-down'}
                      size={iconSize(22)}
                      color={theme.colors.textSecondary}
                    />
                  </Pressable>
                ) : null}
              </View>
              {hasSubs && isOpen
                ? node.subCategories.map(sub => {
                    const on = isSubCategorySelected(draft, node, sub.name);
                    return (
                      <View
                        key={`${key}:${sub.name}`}
                        style={styles.categoryRow}>
                        <Pressable
                          onPress={() =>
                            setDraft(toggleSubCategory(draft, node, sub.name))
                          }
                          accessibilityRole="checkbox"
                          accessibilityState={{ checked: on }}
                          style={({ pressed }) => [
                            styles.categoryLabel,
                            styles.categoryLabelSub,
                            pressed && styles.buttonPressed,
                          ]}>
                          {checkbox(on ? 'on' : 'off')}
                          <Text
                            style={[
                              styles.categoryText,
                              styles.categoryTextSub,
                            ]}
                            numberOfLines={1}>
                            {sub.name}
                          </Text>
                          <Text style={styles.categoryCount}>
                            {sub.products}
                          </Text>
                        </Pressable>
                      </View>
                    );
                  })
                : null}
            </View>
          );
        })}
      </View>
    </InventoryPanel>
  );
};

export default InventoryCategoryPicker;
